import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { each, map } from 'lodash';
import { CardColumnSelectionBase } from 'src/app/classes/base-classes/card-column/card-column-selection-base';
import { AccordionCardCollection } from 'src/app/interfaces/accordion-card-collection';
import { ArraySelectOption } from 'src/app/interfaces/array-select-option';
import { Card } from 'src/app/interfaces/card';
import { Company } from 'src/app/interfaces/models/company';
import { PrimaryTrade } from 'src/app/interfaces/models/primary-trade';
import { TradeCategory } from 'src/app/interfaces/models/trade-category';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { TradeService } from 'src/app/services/trade.service';

import { CardColumnAccordionDisplayModalComponent } from '../card-column-modal/card-column-accordion-display-modal/card-column-accordion-display-modal.component';

@Component({
  selector: 'app-trades-modal',
  templateUrl:
    '../card-column-modal/card-column-accordion-display-modal/card-column-accordion-display-modal.component.html',
  styleUrls: [
    '../card-column-modal/card-column-accordion-display-modal/card-column-accordion-display-modal.component.less',
    './trades-modal.component.less'
  ],
  providers: [SharedDataService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class TradesModalComponent
  extends CardColumnAccordionDisplayModalComponent
  implements OnInit
{
  constructor(
    protected override activeModal: NgbActiveModal,
    protected override ToastService: ToastService,
    protected override ChangeDetectorRef: ChangeDetectorRef,
    protected override SharedDataService: SharedDataService,
    private TradeService: TradeService
  ) {
    super(activeModal, ToastService, ChangeDetectorRef, SharedDataService);
    this.collapsed = true;
  }

  @Input() Companies: Company[] = [];
  @Input() selectedTrades: PrimaryTrade[] | null = null;
  @Input() useTransporterTrades: boolean | null = null;
  tradeCategories!: Required<
    Pick<TradeCategory, 'id' | 'categoryName' | 'PrimaryTrades'>
  >[];

  override ngOnInit(): void {
    if (!Array.isArray(this.Companies) || this.Companies.length === 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client information.',
        { returnError: true }
      );
    }

    this.displaySelectionsAsTags = true;

    const clientLegalNames: string[] = [];
    this.Companies.map((c) => {
      const { legalName } = c;
      if (!legalName) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Client is missing legal name.',
          { returnError: true }
        );
      }
      clientLegalNames.push(legalName);
    });

    this.title = `Select the Trades You Perform for ${clientLegalNames.join(', ')}`;
    this.message = `Type in or scroll through the list to select the trade(s) your business performs. You may select up to 12 trades. The trades chosen will be used to determine if a trade license is required.`;
    this.enableFiltering = true;

    const primaryTradeQuery: {
      options: { required: boolean };
      isTransporter?: boolean;
    } = {
      options: { required: true }
    };
    if (this.useTransporterTrades !== null) {
      primaryTradeQuery.isTransporter = this.useTransporterTrades;
    }

    this.TradeService.getTradeCategories({
      PrimaryTrade: JSON.stringify(primaryTradeQuery),
      order: JSON.stringify([
        ['categoryName', 'ASC'],
        ['PrimaryTrades', 'name', 'ASC']
      ])
    }).subscribe({
      next: (tradeCategories) => {
        this.tradeCategories = tradeCategories as Required<
          Pick<TradeCategory, 'id' | 'categoryName' | 'PrimaryTrades'>
        >[];

        // We want to create a unique collection of trades. The reason being, the same trade
        // can appear multiple times under different trade categories. We want to be able to
        // select/deselect a trade by reference so that ALL trades with the same id under all
        // categories are selected/deselected.
        const uniqueTradeCards: Map<string, Card> = new Map<string, Card>();
        each(tradeCategories, (category) => {
          const { PrimaryTrades } = category;
          each(PrimaryTrades, (trade) => {
            if (!trade.name) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Trade was loaded without a name.'
              );
            }
            if (!uniqueTradeCards.has(trade.name)) {
              uniqueTradeCards.set(trade.name, {
                bodyText: trade.name,
                record: this.selectedTrades
                  ? ({
                      value: trade,
                      comparator: (compareValue, selectedValue) =>
                        compareValue.id === selectedValue.id
                    } as ArraySelectOption<PrimaryTrade>)
                  : undefined
              });
            }
          });
        });

        this.accordionCardCollectionArray = map(tradeCategories, (category) => {
          const { PrimaryTrades } = category;
          return {
            title: category.categoryName!,
            cardCollection: map(PrimaryTrades, (t) => {
              if (!t.name) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Trade was loaded without a name.'
                );
              }
              const card: Card | undefined = uniqueTradeCards.get(t.name);
              if (!card) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Trades were not configured properly.',
                  { returnError: true }
                );
              }
              return card;
            })
          };
        });

        if (this.selectedTrades) {
          this.selectionModel = new CardColumnSelectionBase(
            'multiple',
            this.selectedTrades,
            { selectionLimit: 12, ignoreDuplicatesForSelectionLimit: true }
          );
        }

        this.filterFunction = (filterText: string) => {
          // Utilize case insensitivity when performing filtering.
          const formattedFilterText = filterText.toLowerCase();
          each(
            this.accordionCardCollectionArray,
            (accordionCardCollection: AccordionCardCollection) => {
              const { title, cardCollection } = accordionCardCollection;

              if (!title.toLowerCase().includes(formattedFilterText)) {
                // If at least one card isn't hidden, don't hide the accordion dropdown.
                // If all cards are hidden, hide the accordion dropdown.
                let areAllCardsHidden = true;
                each(cardCollection, (card: Card) => {
                  if (
                    card.bodyText &&
                    !card.bodyText.toLowerCase().includes(formattedFilterText)
                  ) {
                    Object.assign(card, { isHidden: true });
                  } else {
                    Object.assign(card, { isHidden: false });
                    areAllCardsHidden = false;
                  }
                });

                if (areAllCardsHidden) {
                  Object.assign(accordionCardCollection, { hidden: true });
                } else {
                  Object.assign(accordionCardCollection, { hidden: false });
                }
              } else {
                Object.assign(accordionCardCollection, { hidden: false });
                cardCollection.forEach((card: Card) => {
                  Object.assign(card, { isHidden: false });
                });
              }
            }
          );
        };

        super.ngOnInit();
        this.ChangeDetectorRef.markForCheck();
      }
    });
  }
}
